import { useCallback, useEffect, useMemo, useState } from "react";
import { setIsLoading, setShowErrorModal } from "reducers/main";
import {
  useGetTasksInfiniteQuery,
  usePatchTaskByIdMutation,
} from "services/task-api";
import type { RelativeDatePeriod } from "utils/date-utils";
import { getRelativeDateISO } from "utils/date-utils";
import { useDispatch } from "utils/redux";

import type { ApiError, TaskStatus } from "../utils/taskUtils";

import type { TaskDataType } from "types/DataTypes";

type UseTaskBoardDataParams = {
  filters: Record<string, unknown>;
  searchTerm: string;
  userId?: number;
  accessToken?: string;
};

type TaskItems = Record<TaskStatus, TaskDataType[]>;

const getCompletedTasksFilter = (
  filters: Record<string, unknown>,
): RelativeDatePeriod | undefined => {
  const filterValue = filters.completed_tasks_filter;

  if (!Array.isArray(filterValue) || filterValue.length === 0) {
    return undefined;
  }

  return filterValue[0] as RelativeDatePeriod;
};

/* manage task board queries, status updates, and drag/drop mutation flow. */
export const useTaskBoardData = ({
  filters,
  searchTerm,
  userId,
  accessToken,
}: UseTaskBoardDataParams) => {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState<string[]>([]);
  const [patchTaskById] = usePatchTaskByIdMutation();

  const pendingTasks = useGetTasksInfiniteQuery(
    {
      searchTerm,
      filters,
      assignee_id: userId,
      status: "P",
    },
    {
      skip: !userId,
    },
  );

  const inProgressTasks = useGetTasksInfiniteQuery(
    {
      searchTerm,
      filters,
      assignee_id: userId,
      status: "I",
    },
    {
      skip: !userId,
    },
  );

  const completedAfterDate = useMemo(() => {
    const completedTasksFilter = getCompletedTasksFilter(filters);

    if (!completedTasksFilter) {
      return getRelativeDateISO("past_month");
    }

    return getRelativeDateISO(completedTasksFilter);
  }, [filters]);

  const completedTasksFilters = useMemo(() => {
    const filteredFilters = Object.fromEntries(
      Object.entries(filters || {}).filter(
        ([key]) => key !== "completed_tasks_filter",
      ),
    );

    return {
      ...filteredFilters,
      ...(completedAfterDate && {
        completed_after: completedAfterDate,
      }),
    };
  }, [JSON.stringify(filters), completedAfterDate]);

  const completeTasks = useGetTasksInfiniteQuery(
    {
      searchTerm,
      filters: completedTasksFilters,
      assignee_id: userId,
      status: "C",
    },
    {
      skip: !userId,
    },
  );

  const isLoading =
    pendingTasks.isLoading ||
    inProgressTasks.isLoading ||
    completeTasks.isLoading;

  const isRefetching =
    (pendingTasks.isFetching &&
      !pendingTasks.isLoading &&
      !pendingTasks.isFetchingNextPage) ||
    (inProgressTasks.isFetching &&
      !inProgressTasks.isLoading &&
      !inProgressTasks.isFetchingNextPage) ||
    (completeTasks.isFetching &&
      !completeTasks.isLoading &&
      !completeTasks.isFetchingNextPage);

  const items: TaskItems = {
    P: pendingTasks.data?.pages.flatMap((page) => page.results) || [],
    I: inProgressTasks.data?.pages.flatMap((page) => page.results) || [],
    C: completeTasks.data?.pages.flatMap((page) => page.results) || [],
  };

  useEffect(() => {
    dispatch(setIsLoading(false));
  }, [dispatch]);

  const handleChangeTaskStatus = useCallback(
    async (taskId: string, newStatus: TaskStatus) => {
      if (!taskId || !accessToken) {
        return;
      }

      return await patchTaskById({
        accessToken,
        id: taskId,
        data: { status: newStatus },
      }).unwrap();
    },
    [patchTaskById, accessToken],
  );

  const handleApiError = useCallback(
    (error: ApiError) => {
      console.error(error);
      const errorMessages = error.data?.errors?.map((err) => err.message) || [
        "An unknown error occurred",
      ];
      setErrors(errorMessages);
      dispatch(setShowErrorModal(true));
    },
    [dispatch],
  );

  const moveItem = useCallback(
    async (
      itemId: number,
      _sourceColumn: TaskStatus,
      targetColumn: TaskStatus,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _targetIndex: number,
    ) => {
      try {
        await handleChangeTaskStatus(itemId.toString(), targetColumn);
      } catch (error) {
        handleApiError(error as ApiError);
      }
    },
    [handleChangeTaskStatus, handleApiError],
  );

  return {
    completeTasks,
    errors,
    inProgressTasks,
    isLoading,
    isRefetching,
    items,
    moveItem,
    pendingTasks,
  };
};
